import { useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import type { VisualizationData } from '../../types';
import { formatNumber, cn } from '../../lib/utils';

interface DataTableProps {
  data: VisualizationData;
}

type SortDirection = 'asc' | 'desc' | null;

export function DataTable({ data }: DataTableProps) {
  const { config, data: tableData } = data;
  const { title } = config;

  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  const columns = tableData[0] ? Object.keys(tableData[0]) : [];

  const sortedData = [...tableData].sort((a, b) => {
    if (!sortColumn || !sortDirection) return 0;

    const aVal = a[sortColumn];
    const bVal = b[sortColumn];

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    }

    const aStr = String(aVal || '');
    const bStr = String(bVal || '');
    return sortDirection === 'asc'
      ? aStr.localeCompare(bStr)
      : bStr.localeCompare(aStr);
  });

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortColumn(null);
        setSortDirection(null);
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const formatCellValue = (value: unknown): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'number') return formatNumber(value);
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  };

  const formatColumnName = (name: string): string => {
    return name
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <div className="w-full">
      {title && (
        <h3 className="mb-4 text-sm font-medium text-foreground">{title}</h3>
      )}
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              {columns.map((column) => (
                <th
                  key={column}
                  className="cursor-pointer px-4 py-3 text-left font-medium text-muted-foreground hover:text-foreground"
                  onClick={() => handleSort(column)}
                >
                  <div className="flex items-center gap-1">
                    <span>{formatColumnName(column)}</span>
                    <span className="flex flex-col">
                      <ChevronUp
                        className={cn(
                          'h-3 w-3 -mb-1',
                          sortColumn === column && sortDirection === 'asc'
                            ? 'text-foreground'
                            : 'text-muted-foreground/30'
                        )}
                      />
                      <ChevronDown
                        className={cn(
                          'h-3 w-3',
                          sortColumn === column && sortDirection === 'desc'
                            ? 'text-foreground'
                            : 'text-muted-foreground/30'
                        )}
                      />
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedData.slice(0, 50).map((row, rowIndex) => (
              <tr key={rowIndex} className="hover:bg-muted/30 transition-colors">
                {columns.map((column) => (
                  <td
                    key={column}
                    className="px-4 py-3 text-foreground"
                    title={String(row[column] || '')}
                  >
                    {formatCellValue(row[column])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {tableData.length > 50 && (
          <div className="border-t border-border bg-muted/30 px-4 py-2 text-center text-xs text-muted-foreground">
            Showing 50 of {tableData.length} rows
          </div>
        )}
      </div>
    </div>
  );
}
